import React, { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { func } from 'prop-types';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Components
import ExplicitConfirmationDialog from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import ConfirmSubscriptionCard from '../components/confirm-subscription/ConfirmSubscriptionCard';
import NavigationButtons from '../components/navigation-buttons/NavigationButtons';
import SubscriptionHeader from '../components/subscription-header/SubscriptionHeader';

// Context
import { SubscriptionsContext } from '../SubscriptionsContext';

const SubscriptionConfirmation = props => {
  const { createSubscriptionPlan, resetSubscriptions } = props;
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    currentStep,
    data: {
      effectiveDate,
      endDate,
      planSelected,
      globalAffiliatesVolumes,
      globalCustomer,
      selectedAffiliate,
    },
    initialData,
    setCurrentStep,
    setData,
  } = useContext(SubscriptionsContext);
  useEffect(() => {
    if (currentStep !== 3) setCurrentStep(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => {
    navigate('../step2');
    setCurrentStep(2);
  };

  const handleConfirm = () => {
    const { tierPlanName } = planSelected;
    const tierNumber = parseInt(tierPlanName.split(' ')[1], 10);
    createSubscriptionPlan({
      globalCustomer,
      selectedAffiliate,
      tierNumber,
    }).then(() => {
      setData(initialData);
      resetSubscriptions();
      navigate(`..`);
    });
  };

  const formatCost = cost =>
    new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(cost);

  return (
    <Box mb={!mdUp ? theme.spacing(6) : 0}>
      <SubscriptionHeader
        disabled={!selectedAffiliate?.nameShort}
        handleLeftButtonClick={handleGoBack}
        handleRightButtonClick={() => setDialogOpen(true)}
        leftLabel="Back"
        rightLabel="Enroll"
        title="Confirm Your Swift+ Bundle"
      />
      <Box display="flex" justifyContent="center">
        {!R.isEmpty(globalAffiliatesVolumes) && (
          <ConfirmSubscriptionCard
            affiliateList={globalAffiliatesVolumes?.volumes?.twelveMonthVolume?.affiliatesVolume}
            effectiveDate={effectiveDate}
            endDate={endDate}
            onEnroll={() => setDialogOpen(true)}
            onGoBack={handleGoBack}
            planSelected={planSelected}
            selectedAffiliate={selectedAffiliate}
            setData={setData}
          />
        )}
      </Box>
      {dialogOpen && (
        <ExplicitConfirmationDialog
          confirmationMessage="If you want to complete your Swift+ enrollment, enter the word below and click Confirm."
          confirmationValue="ENROLL"
          dialogOpen={dialogOpen}
          handleCloseDialog={() => setDialogOpen(false)}
          label="Enroll"
          onConfirm={handleConfirm}
          title="Confirm Your Enrollment"
          warningList={[
            `You are about to enroll in Swift+ for one year for ${formatCost(
              planSelected.tierFixedFee
            )}.`,
          ]}
        />
      )}
      {!mdUp && (
        <NavigationButtons
          disabled={!selectedAffiliate?.nameShort}
          handleLeftButtonClick={handleGoBack}
          handleRightButtonClick={() => setDialogOpen(true)}
          leftLabel="Back"
          rightLabel="Enroll"
        />
      )}
    </Box>
  );
};

SubscriptionConfirmation.propTypes = {
  createSubscriptionPlan: func.isRequired,
  resetSubscriptions: func.isRequired,
};

export default SubscriptionConfirmation;
